import { useEffect, useRef, useState } from 'react';

import { getTagRule } from '../services/tags';

/** How often a running pass is asked about, and for how long (⚑ T6a web). */
// A pass that is WAITING (it cannot run yet; the server asks again every ten
// minutes) is asked about every 30 s instead.
export const PASS_POLL = Object.freeze({ ms: 2000, waitingMs: 30000, max: 150 });

/**
 * FOLLOWS AN AUTOMATIC TAG'S PASS (docs/plans/tags.md §3.4; keepr-api
 * utils/tagPasses.js). Saving, pausing, resuming or stopping a rule starts a
 * pass on the server whose first slice runs in the request; a large card
 * continues on the scheduler, a minute a slice. While `pass.running`, this
 * asks `GET /api/tags/{id}/rule` for the latest pass every PASS_POLL.ms and
 * hands it back; it stops when the pass does, when the caller unmounts, or
 * after PASS_POLL.max asks (⚑ five minutes — `gaveUp` then says so, and the
 * pass carries on without anyone watching).
 *
 * → `{ pass, gaveUp }`.
 */
export default function useTagRulePass(tagId, initialPass) {
	const [pass, setPass] = useState(initialPass || null);
	const [gaveUp, setGaveUp] = useState(false);
	// One tick per ask, so the next ask is scheduled whatever the answer was.
	const [tick, setTick] = useState(0);
	const asks = useRef(0);

	// A new pass from the caller (another save) replaces what was followed.
	useEffect(() => {
		setPass(initialPass || null);
		setGaveUp(false);
		asks.current = 0;
	}, [initialPass]);

	const running = pass?.running === true;
	const waiting = Boolean(pass?.waiting);
	useEffect(() => {
		if (!tagId || !running || gaveUp) return undefined;
		let active = true;
		const timer = setTimeout(async () => {
			asks.current += 1;
			try {
				const answer = await getTagRule(tagId);
				// Nothing answered keeps the pass as it was (the next ask retries).
				if (active && answer.pass) setPass(answer.pass);
			} catch (error) {
				// A failed ask is retried on the next tick; the cap still holds.
			}
			if (!active) return;
			if (asks.current >= PASS_POLL.max) setGaveUp(true);
			else setTick(n => n + 1);
		}, waiting ? PASS_POLL.waitingMs : PASS_POLL.ms);
		return () => { active = false; clearTimeout(timer); };
	}, [tagId, running, waiting, gaveUp, tick]);

	return { pass, gaveUp };
}
